import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from './Navigation';
import LanguageSelector from './LanguageSelector';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/**
 * Header component with logo, navigation menu, and search bar
 * Implements sticky header behavior (Requirements 4.1, 4.3)
 */
const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const localizedPath = useLocalizedPath();

  // Handle scroll for sticky header with shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(localizedPath(`/search?q=${encodeURIComponent(searchQuery.trim())}`));
      setSearchQuery('');
    }
  };

  return (
    <header 
      className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      {/* Top bar with logo and search */}
      <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-primary-700 text-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and site title */}
            <Link to={localizedPath('/')} className="flex items-center space-x-4 hover:opacity-95 transition-all duration-300 group">
              {/* Logo with enhanced styling */}
              <div className="relative">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full p-1 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/vpkhcn-logo.png" 
                    alt="VASTP Logo" 
                    className="w-full h-full object-contain rounded-full"
                    onError={(e) => {
                      // Fallback if image not found
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full rounded-full bg-white hidden items-center justify-center">
                    <span className="text-primary-600 font-bold text-2xl md:text-3xl">V</span>
                  </div>
                </div>
                {/* Decorative ring */}
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-pulse"></div>
              </div>
              
              {/* Association name with enhanced typography */}
              <div className="flex flex-col">
                <h1 className="text-2xl md:text-3xl font-bold leading-tight tracking-wide text-white drop-shadow-md">
                  {t('common:site.title')}
                </h1>
                <p className="text-xs md:text-sm text-primary-50 font-medium leading-tight mt-1 max-w-xs md:max-w-md">
                  {t('common:site.subtitle')}
                </p>
              </div>
            </Link>

            {/* Search bar - desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <form onSubmit={handleSearchSubmit} className="flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('navigation:search.placeholder')}
                    className="w-64 xl:w-80 px-4 py-2.5 pr-10 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-md"
                    aria-label={t('navigation:search.placeholder')}
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-primary-600 transition-colors"
                    aria-label={t('navigation:search.submitLabel')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </form>
              <LanguageSelector />
            </div>
          </div>

          {/* Search bar - mobile */}
          <div className="lg:hidden mt-4 flex items-center space-x-2">
            <form onSubmit={handleSearchSubmit} className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('navigation:search.placeholder')}
                  className="w-full px-4 py-2.5 pr-10 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-md"
                  aria-label={t('navigation:search.placeholder')}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-primary-600 transition-colors"
                  aria-label={t('navigation:search.submitLabel')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Navigation menu */}
      <Navigation currentPath={location.pathname} />
    </header>
  );
};

export default Header;
